import { createSelector, createStructuredSelector } from 'reselect';
import { movieInfo } from 'selectors/movie';
import _ from 'lodash';

const getTariffInfo = (rawTariff = {}, currency) => ({
  id: rawTariff.tariff_id,
  title: rawTariff.title,
  type: 'svod',
  isTryAndBuy: rawTariff.is_try_and_buy,
  regularPrice: rawTariff.regular_price,
  regularPriceText: `${rawTariff.regular_price} ${currency}`,
  period: rawTariff.period,
  priceText: `${rawTariff.price} ${currency}`,
  price: rawTariff.price,
  rulesLink: rawTariff.rules_link,
  landingLink: rawTariff.landing_link,
  paymentSystems: rawTariff.payment_systems
    ? rawTariff.payment_systems.map((system) => system.payment_system_id)
    : [],
  currency,
});

const tariffIdPropSelector = (state, { tariffId }) => tariffId;

export const subscription = (state) => state.subscription;

export const subscriptionArray = createSelector(subscription, movieInfo, (subs, movie) => {
  let result;
  const subscriptions = _.get(subs, 'subscriptions');
  const movieSubs = _.get(movie, 'purchase_info.svod.subscriptions');
  if (subscriptions) {
    result = _.filter(subscriptions, (sub) => _.includes(movieSubs, sub.subscription_id));
  }
  return result;
});

export const subscriptionMap = createSelector(subscriptionArray, (subs) => {
  let result;
  if (!_.isEmpty(subs)) {
    result = subs.reduce(
      (accum, obj) => ({
        ...accum,
        [obj.subscription_id]: obj,
      }),
      {}
    );
  }
  return result;
});

export const subscriptionIsLoaded = createSelector(subscription, (subs) => _.get(subs, 'isLoaded'));

export const subscriptionIsLoading = createSelector(subscription, (subs) =>
  _.get(subs, 'isLoading')
);

export const needToLoadSubscription = createSelector(
  subscription,
  movieInfo,
  (subs, movie) =>
    movie &&
    _.includes(movie.delivery_rules, 'svod') &&
    _.get(movie, 'purchase_info.svod.subscriptions.0') && // this is okay, that we are looking just for first element, coz we need just fact of availability of ids
    !_.get(subs, 'isLoaded') &&
    !_.get(subs, 'isError') &&
    !_.get(subs, 'isLoading')
);

export const subscriptionInfo = createSelector(movieInfo, subscriptionArray, (movie, subsArray) => {
  let result = {};
  const subs = _.get(subsArray, '0');
  if (subs) {
    result = {
      title: subs.title,
      isBought: subs.is_bought,
      expirationDate: _.get(subs, 'expiration_date', ''),
      description: subs.description_full,
      promo: subs.promo_phrase,
      collectionId: _.get(
        _.find(_.get(subs, 'subscription_meta.collections'), { type: 'sale' }),
        'collection_id'
      ),
      currency: subs.currency,
      tariffs: [],
    };
    const tariffs = _.get(subs, 'tariffs');
    for (let i = 0, l = tariffs.length; i < l; ++i) {
      result.tariffs.push(getTariffInfo(tariffs[i], result.currency));
    }
  }
  return result;
});

export const expirationDateInfo = createSelector(subscriptionInfo, (info) => {
  if (_.get(info, 'expirationDate')) {
    return _.get(info, 'expirationDate')
      .substr(0, 10)
      .replace(/-/g, '.')
      .split('.')
      .reverse()
      .join('.');
  }
  return '';
});

export const isSVODBoughtInfo = createSelector(subscriptionInfo, (info) =>
  _.get(info, 'isBought', false)
);

export const subscriptionTariff = createSelector(
  tariffIdPropSelector,
  subscriptionMap,
  (tariffId, subscriptions) => {
    let result = null;
    if (subscriptions) {
      const keys = Object.keys(subscriptions);
      let tariffs;
      for (let i = 0, l = keys.length; i < l; ++i) {
        tariffs = _.get(subscriptions, `${keys[i]}.tariffs`);
        if (_.isArray(tariffs)) {
          for (let j = 0, k = tariffs.length; j < k; ++j) {
            if (tariffs[j].tariff_id === tariffId) {
              result = getTariffInfo(tariffs[j], subscriptions[keys[i]].currency);
              break;
            }
          }
        }
        if (result) {
          break;
        }
      }
    }
    return result;
  }
);

export default createStructuredSelector({
  subscription,
  expirationDateInfo,
  isSVODBoughtInfo,
  subscriptionMap,
  subscriptionArray,
  subscriptionIsLoaded,
  subscriptionIsLoading,
  needToLoadSubscription,
  subscriptionInfo,
  subscriptionTariff,
});
